import React from 'react';

/**
 * Bare-URL detection for plain-text message bodies.
 *
 * Only http/https is matched — never `javascript:`, `data:` or any other
 * scheme — so a crafted message body can't produce a script-executing anchor.
 */
const URL_PATTERN = /\bhttps?:\/\/[^\s<>"']+/gi;

// Sentence punctuation that almost always belongs to the prose, not the URL.
const TRAILING_PUNCTUATION = /[.,;:!?'"]+$/;

/**
 * Trim trailing characters that a writer's sentence contributed rather than
 * the URL itself: "see https://a.com/docs." → the period is not part of it.
 * A closing paren is only dropped when the URL has no matching opener, so
 * Wikipedia-style URLs like `/wiki/React_(framework)` survive intact.
 */
const trimTrailing = (url) => {
  let result = url.replace(TRAILING_PUNCTUATION, '');

  while (/[)\]]$/.test(result)) {
    const close = result.slice(-1);
    const open = close === ')' ? '(' : '[';
    const opens = result.split(open).length - 1;
    const closes = result.split(close).length - 1;
    if (closes <= opens) break; // balanced — the paren belongs to the URL
    result = result.slice(0, -1).replace(TRAILING_PUNCTUATION, '');
  }

  return result;
};

/**
 * Convert bare URLs inside plain text into anchors, leaving all other text
 * untouched. Returns the input unchanged when it is empty or has no URLs, so
 * callers can drop this in without changing their non-URL rendering path.
 *
 * Links always open in a new tab: the app is embedded in an iframe
 * (member.kiln.com/concierge), so a same-frame navigation would replace the
 * whole app with the target page. `rel="noopener noreferrer"` keeps the
 * opened tab from reaching back into `window.opener`.
 *
 * @param {string} text
 * @returns {string|Array<React.ReactNode>} original text, or mixed nodes
 */
export const linkifyText = (text) => {
  if (!text || typeof text !== 'string') return text;

  const matches = [...text.matchAll(URL_PATTERN)];
  if (matches.length === 0) return text;

  const nodes = [];
  let cursor = 0;

  matches.forEach((match, idx) => {
    const raw = match[0];
    const href = trimTrailing(raw);
    if (!href) return;

    const start = match.index;
    if (start > cursor) {
      nodes.push(
        <React.Fragment key={`text-${idx}`}>{text.slice(cursor, start)}</React.Fragment>
      );
    }

    nodes.push(
      <a
        key={`link-${idx}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="linkified-url"
      >
        {href}
      </a>
    );

    cursor = start + href.length;
  });

  if (cursor < text.length) {
    nodes.push(<React.Fragment key="text-end">{text.slice(cursor)}</React.Fragment>);
  }

  return nodes;
};

export default linkifyText;
